import React, {useEffect, useState} from 'react';
import {
    Create,
    Edit,
    FileField,
    FileInput,
    FormDataConsumer,
    ImageField,
    ImageInput,
    SimpleForm,
    TextInput,
    useGetIdentity,
    useRecordContext,
    usePermissions,
    useSaveContext,
    useTranslate,
} from 'react-admin';
import {useFormContext} from 'react-hook-form';
import {useLocation} from 'react-router-dom';
import {Alert, AlertTitle, Avatar, Box, Typography} from '@mui/material';
import {DeleteOutline, TextSnippet, PhotoOutlined, VideocamOutlined} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import moment from 'moment';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import StatusDot from '../components/statamic/StatusDot';
import StatamicTextInput from '../components/statamic/StatamicTextInput';
import StatamicSelectInput from '../components/statamic/StatamicSelectInput';
import ToggleRow from '../components/statamic/ToggleRow';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import canDeletePost from './canDeletePost';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import {useCharityChoicesNumericV1} from '../lib/customHooks';

const STATUS_CHOICES = (translate) => [
    {id: 'PENDING_APPROVAL', name: translate('Pending Approval')},
    {id: 'PUBLISHED',        name: translate('Published')},
    {id: 'REJECTED',         name: translate('Rejected')},
];

const statusVariant = (status) => {
    switch (status) {
        case 'PUBLISHED':        return 'success';
        case 'PENDING_APPROVAL': return 'warning';
        case 'REJECTED':         return 'danger';
        default:                 return 'neutral';
    }
};

const SaveAction = ({label}) => {
    const {handleSubmit} = useFormContext();
    const {save, saving} = useSaveContext();
    return (
        <PrimaryButton variant="primary" disabled={saving} onClick={handleSubmit(save)}>
            {saving ? 'Saving…' : label}
        </PrimaryButton>
    );
};

const MessageTypePill = ({icon: Icon, label, selected, onClick}) => (
    <Box
        onClick={onClick}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.5,
            py: 1,
            borderRadius: `${t.radius.sm}px`,
            border: `1px solid ${selected ? t.color.accent.primary : t.color.border.default}`,
            backgroundColor: selected ? t.color.accent.primaryTint : t.color.surface,
            color: selected ? t.color.accent.primary : t.color.text.primary,
            fontSize: `${t.type.body.size}px`,
            fontWeight: selected ? 600 : 500,
            cursor: 'pointer',
            transition: 'border-color 120ms ease, background-color 120ms ease',
            '&:hover': {
                borderColor: selected ? t.color.accent.primary : t.color.border.strong,
            },
        }}
    >
        <Icon sx={{fontSize: 16}}/>
        {label}
    </Box>
);

const MessageTypeSelector = ({choices, translate}) => {
    const {watch, setValue} = useFormContext();
    const current = watch('message_type');

    const iconFor = (id) => {
        if (id === 'TEXT')  return TextSnippet;
        if (id === 'PHOTO') return PhotoOutlined;
        if (id === 'VIDEO') return VideocamOutlined;
        return TextSnippet;
    };

    return (
        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
            {choices.map(c => (
                <MessageTypePill
                    key={c.id}
                    icon={iconFor(c.id)}
                    label={c.name}
                    selected={current === c.id}
                    onClick={() => setValue('message_type', c.id, {shouldDirty: true})}
                />
            ))}
        </Box>
    );
};

const CurrentMedia = ({record}) => {
    const isPhoto = record.media_type === 'photo' || record.message_type === 'PHOTO';
    const isVideo = record.media_type === 'video' || record.message_type === 'VIDEO';
    const isText  = record.message_type === 'TEXT';

    if (isText) {
        return (
            <Alert severity="info" sx={{fontSize: `${t.type.small.size}px`}}>
                <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>Text-only post</AlertTitle>
                This post contains only text content, no media.
            </Alert>
        );
    }
    if (isPhoto) {
        return (
            <Box
                component="img"
                src={`${process.env.REACT_APP_CDN_URL}/${record.name}.${record.ext}`}
                alt={record.description || 'Post photo'}
                sx={{
                    width: '100%',
                    maxHeight: 420,
                    objectFit: 'contain',
                    borderRadius: `${t.radius.sm}px`,
                    backgroundColor: '#000',
                }}
            />
        );
    }
    if (isVideo) {
        return (
            <Box sx={{borderRadius: `${t.radius.sm}px`, overflow: 'hidden', backgroundColor: '#000'}}>
                <ReactPlayer
                    url={`${process.env.REACT_APP_CDN_URL}/${record.name}.${record.ext}`}
                    controls
                    width="100%"
                    height="auto"
                />
            </Box>
        );
    }
    return null;
};

const ChildPreview = ({child}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: `${t.radius.md}px`,
            border: `1px solid ${t.color.border.default}`,
        }}
    >
        <Avatar
            src={child.profile_image && child.image_ext
                ? `${process.env.REACT_APP_CDN_URL}/${child.profile_image}.${child.image_ext}`
                : undefined}
            sx={{
                width: 48, height: 48,
                bgcolor: t.color.sidebarHover,
                color: t.color.text.secondary,
                border: `1px solid ${t.color.border.hairline}`,
            }}
        />
        <Box sx={{minWidth: 0, flex: 1}}>
            <Typography sx={{fontSize: `${t.type.bodyStrong.size}px`, fontWeight: 500, color: t.color.text.primary}}>
                {child.name || `${child.first_name} ${child.last_name}`}
            </Typography>
            {child.charity?.charity_name && (
                <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                    {child.charity.charity_name}
                </Typography>
            )}
        </Box>
    </Box>
);

const FormBody = ({mode}) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const {identity} = useGetIdentity();
    const charityChoices = useCharityChoicesNumericV1();
    const location = useLocation();
    const deleteConfirm = useDeleteConfirm();

    const [child, setChild] = useState(null);

    useEffect(() => {
        const routeChild = location.state?.child;
        if (routeChild) setChild(routeChild);
        else if (record?.child) setChild(record.child);
    }, [location, record]);

    if (!charityChoices) return null;
    if (mode === 'edit' && !record) return null;

    // Message-type availability is a property of the SELECTED CHILD's charity, not of
    // some arbitrary first charity in the admin's list. Look the charity up by the
    // child's charity_id; fall back to the first choice (covers single-charity admins).
    const childCharityId = child?.charity_id ?? child?.charity?.id;
    // childCharityId may be either the v1 numeric charity id or (v2 create-from-child
    // flows) the charity's UUID external_id — match against whichever shape this
    // numeric-keyed choices list carries for the id.
    const charitySettings =
        (childCharityId != null && charityChoices.find(c =>
            String(c.id) === String(childCharityId)
            || (c.external_id && String(c.external_id) === String(childCharityId))
        ))
        || (charityChoices.length > 0 ? charityChoices[0] : null);
    const messageTypeChoices = [];
    if (charitySettings?.allow_text_messages)  messageTypeChoices.push({id: 'TEXT',  name: translate('Text only')});
    if (charitySettings?.allow_photo_messages) messageTypeChoices.push({id: 'PHOTO', name: translate('Photo')});
    if (charitySettings?.allow_video_messages) messageTypeChoices.push({id: 'VIDEO', name: translate('Video')});
    const defaultMessageType = messageTypeChoices.length > 0 ? messageTypeChoices[0].id : null;

    const isAdmin = permissions === 'admin' || permissions === 'umbrella';

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                breadcrumb={{label: translate('Posts'), to: '/posts'}}
                title={
                    mode === 'create' ? (
                        <Box component="span">{translate('Share new update post')}</Box>
                    ) : (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <StatusDot variant={statusVariant(record?.status)}/>
                            <Box component="span">
                                {translate('Edit')}
                                <Box component="span" sx={{color: t.color.text.secondary, fontWeight: 400}}>
                                    {' '}· {record?.creator?.given_name} · {moment(record?.created_time).fromNow()}
                                </Box>
                            </Box>
                        </Box>
                    )
                }
                actions={
                    <>
                        <SaveAction label={mode === 'create' ? translate('Publish') : translate('Save changes')}/>
                        {mode === 'edit' && canDeletePost(permissions, record, identity?.id) && (
                            <PrimaryButton
                                variant="danger"
                                startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                                onClick={deleteConfirm.openDialog}
                            >
                                {translate('Delete')}
                            </PrimaryButton>
                        )}
                    </>
                }
            />

            {record && (
                <DeleteConfirmDialog
                    open={deleteConfirm.open}
                    onClose={deleteConfirm.closeDialog}
                    resource="posts"
                    record={record}
                    recordLabel={`post by ${record.creator?.given_name ?? 'unknown'}`}
                />
            )}

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: '1fr 340px', gap: 2, alignItems: 'start'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    {mode === 'create' && !child && (
                        <Alert severity="warning" sx={{fontSize: `${t.type.small.size}px`}}>
                            <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>{translate('Attention')}</AlertTitle>
                            {translate('First select a child for new update post')}
                        </Alert>
                    )}

                    {mode === 'create' && child && messageTypeChoices.length === 0 && (
                        <Alert severity="error" sx={{fontSize: `${t.type.small.size}px`}}>
                            <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>{translate('Error')}</AlertTitle>
                            {translate('No message types are available for this charity. Please contact your administrator.')}
                        </Alert>
                    )}

                    {mode === 'create' && child && messageTypeChoices.length > 0 && (
                        <>
                            <SectionCard title={translate('Message type')}>
                                <MessageTypeSelector choices={messageTypeChoices} translate={translate}/>
                                <TextInput source="message_type" defaultValue={defaultMessageType} sx={{display: 'none'}}/>
                            </SectionCard>

                            <FormDataConsumer>
                                {({formData}) => (
                                    <SectionCard title={formData.message_type === 'TEXT' ? translate('Message') : translate('Content')}>
                                        <StatamicTextInput
                                            source="description"
                                            label={formData.message_type === 'TEXT' ? translate('Message') : translate('Description')}
                                            required={formData.message_type === 'TEXT'}
                                            multiline
                                            rows={formData.message_type === 'TEXT' ? 6 : 3}
                                            helperText={formData.message_type === 'TEXT'
                                                ? translate('Enter your text message here')
                                                : translate('Optional description for the media')}
                                        />
                                        {formData.message_type !== 'TEXT' && (
                                            <Box sx={{mt: 1.5}}>
                                                <ToggleRow
                                                    source="portrait_mode"
                                                    label={translate('Portrait mode')}
                                                    subtitle="Use portrait (vertical) orientation"
                                                    defaultValue={false}
                                                />
                                            </Box>
                                        )}
                                        {formData.message_type === 'PHOTO' && (
                                            <Box sx={{mt: 2}}>
                                                <input type="hidden" name="media_type" value="photo"/>
                                                <ImageInput
                                                    source="image"
                                                    label={translate('Post image')}
                                                    accept={{'image/*': []}}
                                                    maxSize={3000000}
                                                    sx={{
                                                        '& .RaFileInput-dropZone': {
                                                            border: `1px dashed ${t.color.border.default}`,
                                                            borderRadius: `${t.radius.sm}px`,
                                                            backgroundColor: t.color.surface,
                                                            padding: 3,
                                                            textAlign: 'center',
                                                            cursor: 'pointer',
                                                            color: t.color.text.secondary,
                                                            fontSize: `${t.type.caption.size}px`,
                                                            '&:hover': {
                                                                borderColor: t.color.accent.primary,
                                                                color: t.color.accent.primary,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <ImageField source="src" title="Image"/>
                                                </ImageInput>
                                            </Box>
                                        )}
                                        {formData.message_type === 'VIDEO' && (
                                            <Box sx={{mt: 2}}>
                                                <input type="hidden" name="media_type" value="video"/>
                                                <FileInput
                                                    source="video"
                                                    label={translate('Post video')}
                                                    accept={{'video/*': []}}
                                                    sx={{
                                                        '& .RaFileInput-dropZone': {
                                                            border: `1px dashed ${t.color.border.default}`,
                                                            borderRadius: `${t.radius.sm}px`,
                                                            backgroundColor: t.color.surface,
                                                            padding: 3,
                                                            textAlign: 'center',
                                                            cursor: 'pointer',
                                                            color: t.color.text.secondary,
                                                            fontSize: `${t.type.caption.size}px`,
                                                            '&:hover': {
                                                                borderColor: t.color.accent.primary,
                                                                color: t.color.accent.primary,
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <FileField source="src" title="Video"/>
                                                </FileInput>
                                            </Box>
                                        )}
                                        {formData.message_type === 'TEXT' && (
                                            <Alert severity="info" sx={{mt: 2, fontSize: `${t.type.small.size}px`}}>
                                                {translate('This will be a text-only post without any media')}
                                            </Alert>
                                        )}
                                    </SectionCard>
                                )}
                            </FormDataConsumer>
                        </>
                    )}

                    {mode === 'edit' && (
                        <>
                            <SectionCard title={translate('Message')}>
                                <StatamicTextInput
                                    source="description"
                                    label={translate('Description')}
                                    required
                                    multiline
                                    rows={4}
                                />
                            </SectionCard>

                            <SectionCard title={translate('Content')}>
                                <CurrentMedia record={record}/>
                            </SectionCard>

                            {isAdmin && (
                                <SectionCard title={translate('Post Approval Management')}>
                                    <StatamicSelectInput
                                        source="status"
                                        label={translate('Post Status')}
                                        choices={STATUS_CHOICES(translate)}
                                    />
                                    <FormDataConsumer>
                                        {({formData}) => (
                                            <>
                                                {formData.status === 'REJECTED' && (
                                                    <Box sx={{mt: 1.5}}>
                                                        <StatamicTextInput
                                                            source="rejection_reason"
                                                            label={translate('Rejection Reason')}
                                                            multiline
                                                            rows={3}
                                                            helperText={translate('Provide a clear reason for rejection')}
                                                        />
                                                    </Box>
                                                )}
                                                {record?.approved_by && (
                                                    <Box sx={{mt: 1.5, p: 1.5, border: `1px solid ${t.color.border.hairline}`, borderRadius: `${t.radius.sm}px`}}>
                                                        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                                                            {record.status === 'PUBLISHED' ? translate('Approved by') : translate('Rejected by')}
                                                            {': '}
                                                            <Box component="strong" sx={{color: t.color.text.primary}}>
                                                                {record.approved_by.given_name}
                                                            </Box>
                                                        </Typography>
                                                        <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                                                            {translate('Date')}: {moment(record.approved_at).format('YYYY-MM-DD HH:mm')}
                                                        </Typography>
                                                        {record.rejection_reason && (
                                                            <Typography sx={{mt: 0.5, fontSize: `${t.type.caption.size}px`, color: t.color.status.dangerSolid}}>
                                                                {translate('Reason')}: {record.rejection_reason}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                                {formData.status === 'PUBLISHED' && record?.status !== 'PUBLISHED' && (
                                                    <Alert severity="info" sx={{mt: 1.5, fontSize: `${t.type.small.size}px`}}>
                                                        <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>{translate('Publishing Post')}</AlertTitle>
                                                        {translate('This will make the post visible to all guardians and send notifications.')}
                                                    </Alert>
                                                )}
                                            </>
                                        )}
                                    </FormDataConsumer>
                                </SectionCard>
                            )}
                        </>
                    )}
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 16}}>
                    {child && (
                        <SectionCard title={mode === 'create' ? translate('Share for child') : translate('Post for child')}>
                            <ChildPreview child={child}/>
                            <TextInput source="child_id" defaultValue={child?.id} sx={{display: 'none'}}/>
                        </SectionCard>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export const PostEditStatamic = (props) => (
    <Edit {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="show">
        <SimpleForm toolbar={false}>
            <FormBody mode="edit"/>
        </SimpleForm>
    </Edit>
);

export const PostCreateStatamic = (props) => (
    <Create {...props} actions={false} mutationMode="pessimistic" component={Box} redirect="show">
        <SimpleForm toolbar={false}>
            <FormBody mode="create"/>
        </SimpleForm>
    </Create>
);

export default PostEditStatamic;
